import { EvaChart } from "constants/charts.enums";
import { LegendStates, MouseSelectionStates, SelectedParams } from "types";
import { SeriesType } from "types/echarts";

import { ChartDependencies } from "hooks/charts";

import { boxPlotHoverLegends } from "./boxPlot";
import { boxPlotMidstreamHoverLegends } from "./boxPlotMidstream";
import { cagrHoverLegends, cagrSelectionParams } from "./cagr";
import { crossPlotHoverLegends, crossPlotSelectionParams } from "./crossPlot";
import { cumTimeHoverLegends } from "./cumTime";
import { declineRateHoverLegends, declineRateSelectionParams } from "./declineRate";
import { materialBalanceTimeHoverLegends } from "./materialBalanceTime";
import { mosaicHoverLegends, mosaicSelectionParams } from "./mosaic";
import {
  mosaicMidstreamHoverLegends,
  mosaicMidstreamSelectionParams
} from "./mosaicMidstream";
import { pieHoverLegends, pieSelectionParams } from "./pie";
import { probitHoverLegends, probitSelectionParams } from "./probit";
import { rateCumHoverLegends, rateCumSelectionParams } from "./rateCum";
import { rateDateHoverLegends, rateDateSelectionParams } from "./rateDate";
import {
  rateDateMidstreamHoverLegends,
  rateDateMidstreamSelectionParams
} from "./rateDateMidstream";
import { rateTimeHoverLegends, rateTimeSelectionParams } from "./rateTime";
import { recoveryPVIHoverLegends, recoveryPVISelectionParams } from "./recoveryPVI";
import { recoveryTimeHoverLegends, recoveryTimeSelectionParams } from "./recoveryTime";
import { stackedBarHoverLegends, stackedBarSelectionParams } from "./stackedBar";
import { totalRateCumHoverLegends, totalRateCumSelectionParams } from "./totalRateCum";
import { totalRateDateHoverLegends, totalRateDateSelectionParams } from "./totalRateDate";
import {
  totalRateDateMidstreamHoverLegends,
  totalRateDateMidstreamSelectionParams
} from "./totalRateDateMidstream";
import { trendDateHoverLegends, trendDateSelectionParams } from "./trendDate";
import {
  trendDateMidstreamHoverLegends,
  trendDateMidstreamSelectionParams
} from "./trendDateMidstream";
import {
  wellContributionHoverLegends,
  wellContributionSelectionParams
} from "./wellContribution";

const PARAM_TRANSLATOR_MAP = {
  [EvaChart.RateCum]: rateCumSelectionParams,
  [EvaChart.RecoveryPVI]: recoveryPVISelectionParams,
  [EvaChart.RecoveryTime]: recoveryTimeSelectionParams,
  [EvaChart.CrossPlot]: crossPlotSelectionParams,
  [EvaChart.CumTime]: rateCumSelectionParams,
  [EvaChart.RateTime]: rateTimeSelectionParams,
  [EvaChart.Pie]: pieSelectionParams,
  [EvaChart.RateDate]: rateDateSelectionParams,
  [EvaChart.TotalRateDate]: totalRateDateSelectionParams,
  [EvaChart.TotalRateCum]: totalRateCumSelectionParams,
  [EvaChart.DeclineRate]: declineRateSelectionParams,
  [EvaChart.CAGR]: cagrSelectionParams,
  [EvaChart.Mosaic]: mosaicSelectionParams,
  [EvaChart.StackedBar]: stackedBarSelectionParams,
  [EvaChart.StackedBarHorizontal]: stackedBarSelectionParams,
  [EvaChart.Probit]: probitSelectionParams,
  [EvaChart.TrendDate]: trendDateSelectionParams,
  [EvaChart.InjectorTotal]: trendDateSelectionParams,

  // midstream charts
  [EvaChart.RateDateMidstream]: rateDateMidstreamSelectionParams,
  [EvaChart.TotalRateDateMidstream]: totalRateDateMidstreamSelectionParams,
  [EvaChart.TrendDateMidstream]: trendDateMidstreamSelectionParams,
  [EvaChart.CrossPlotMidstream]: crossPlotSelectionParams,
  [EvaChart.WellContribution]: wellContributionSelectionParams,
  [EvaChart.MosaicMidstream]: mosaicMidstreamSelectionParams,
  [EvaChart.StackedBarMidstream]: stackedBarSelectionParams
};

export function translateLegendsToParams({
  type,
  states,
  series,
  forecast
}: LegendStates): SelectedParams {
  const translator = PARAM_TRANSLATOR_MAP[type];
  if (!translator) {
    return {
      targetIndex: undefined,
      seriesIndex: undefined,
      seriesType: SeriesType.None,
      x: undefined,
      y: undefined
    };
  }
  return translator({ states, series, forecast });
}

const LEGEND_TRANSLATOR_MAP = {
  [EvaChart.RateCum]: rateCumHoverLegends,
  [EvaChart.RecoveryPVI]: recoveryPVIHoverLegends,
  [EvaChart.RecoveryTime]: recoveryTimeHoverLegends,
  [EvaChart.CrossPlot]: crossPlotHoverLegends,
  [EvaChart.CumTime]: cumTimeHoverLegends,
  [EvaChart.RateTime]: rateTimeHoverLegends,
  [EvaChart.Pie]: pieHoverLegends,
  [EvaChart.RateDate]: rateDateHoverLegends,
  [EvaChart.TotalRateDate]: totalRateDateHoverLegends,
  [EvaChart.TotalRateCum]: totalRateCumHoverLegends,
  [EvaChart.DeclineRate]: declineRateHoverLegends,
  [EvaChart.CAGR]: cagrHoverLegends,
  [EvaChart.Mosaic]: mosaicHoverLegends,
  [EvaChart.StackedBar]: stackedBarHoverLegends,
  [EvaChart.StackedBarHorizontal]: stackedBarHoverLegends,
  [EvaChart.BoxPlot]: boxPlotHoverLegends,
  [EvaChart.Probit]: probitHoverLegends,
  [EvaChart.MaterialBalanceTime]: materialBalanceTimeHoverLegends,
  [EvaChart.TrendDate]: trendDateHoverLegends,
  [EvaChart.InjectorTotal]: trendDateHoverLegends,

  // midstream charts
  [EvaChart.RateDateMidstream]: rateDateMidstreamHoverLegends,
  [EvaChart.TotalRateDateMidstream]: totalRateDateMidstreamHoverLegends,
  [EvaChart.TrendDateMidstream]: trendDateMidstreamHoverLegends,
  [EvaChart.CrossPlotMidstream]: crossPlotHoverLegends,
  [EvaChart.WellContribution]: wellContributionHoverLegends,
  [EvaChart.MosaicMidstream]: mosaicMidstreamHoverLegends,
  [EvaChart.StackedBarMidstream]: stackedBarHoverLegends,
  [EvaChart.BoxPlotMidstream]: boxPlotMidstreamHoverLegends
};

export function translateSelectionToLegends({
  type,
  params,
  series,
  response,
  forecast,
  isMultiProductActive
}: MouseSelectionStates): Partial<ChartDependencies> {
  const translator = LEGEND_TRANSLATOR_MAP[type];
  if (!translator) {
    return {
      hoverLegendItem: "",
      hoverLegendGroup: "",
      hoverLegendId: "",
      hoverLegendItemUnfiltered: "",
      attentionWells: []
    };
  }
  return translator({ params, series, response, forecast, isMultiProductActive });
}
